import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { getArticles } from '../services/api';
import ArticleCard from '../components/ArticleCard';
import { CATEGORIES, categoryLabel } from '../constants';
import '../styles/Articles.css';

const Articles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = searchParams.get('category') || 'all';
  const query = searchParams.get('q') || '';
  const [draft, setDraft] = useState(query);

  useEffect(() => { setDraft(query); }, [query]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getArticles()
      .then((r) => { if (alive) setArticles(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setArticles([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  /* only offer sections that actually hold something */
  const availableCats = useMemo(() => {
    const present = new Set(articles.map((a) => a.category));
    return CATEGORIES.filter((c) => present.has(c.key));
  }, [articles]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      if (category !== 'all' && a.category !== category) return false;
      if (!q) return true;
      const haystack = [a.title, a.titleAr, a.content, a.contentAr]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [articles, category, query]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setParam('q', draft.trim());
  };

  const clearAll = () => setSearchParams({}, { replace: true });
  const filtering = category !== 'all' || !!query;

  return (
    <div className="articles-page">
      <header className="page-header">
        <span className="eyebrow">اقرئي على مهلكِ</span>
        <h1>مقالاتنا</h1>
        <p>نصائح وإرشادات مكتوبة بلغة بسيطة، لكل مرحلة من مراحل الأمومة.</p>
      </header>

      <div className="container">
        {/* ---------- search ---------- */}
        <form className="articles-search" onSubmit={onSubmit} role="search">
          <label htmlFor="articles-q" className="sr-only">ابحثي في المقالات</label>
          <FaSearch aria-hidden="true" />
          <input
            id="articles-q"
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="ابحثي بعنوان أو كلمة..."
          />
          <button type="submit">بحث</button>
        </form>

        {/* ---------- section chips ---------- */}
        <nav className="chip-row" aria-label="تصفية حسب القسم">
          <button
            type="button"
            className={'chip' + (category === 'all' ? ' active' : '')}
            onClick={() => setParam('category', 'all')}
            aria-pressed={category === 'all'}
          >
            الكل
          </button>
          {availableCats.map((c) => (
            <button
              key={c.key}
              type="button"
              className={'chip' + (category === c.key ? ' active' : '')}
              onClick={() => setParam('category', c.key)}
              aria-pressed={category === c.key}
            >
              <span aria-hidden="true">{c.icon}</span> {c.name}
            </button>
          ))}
        </nav>

        {/* ---------- state line ---------- */}
        <div className="results-line" aria-live="polite">
          <span className="results-count">
            {loading ? 'جاري التحميل…' : results.length + ' مقال'}
            {category !== 'all' && !loading ? ' في ' + categoryLabel(category) : ''}
            {query && !loading ? ' بحثاً عن «' + query + '»' : ''}
          </span>
          {filtering && (
            <button type="button" className="clear-filters" onClick={clearAll}>
              <FaTimes aria-hidden="true" /> إلغاء التصفية
            </button>
          )}
        </div>

        {/* ---------- results ---------- */}
        {loading ? (
          <div className="articles-grid" aria-hidden="true">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-card skeleton-article">
                <div className="skeleton-img tall" />
                <div className="skeleton-line" />
                <div className="skeleton-line short" />
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="no-articles">
            <span className="empty-mark" aria-hidden="true">🌿</span>
            <p>لم نجد مقالاً يطابق بحثكِ.</p>
            {filtering && (
              <button type="button" className="btn btn-ghost" onClick={clearAll}>
                عرض كل المقالات
              </button>
            )}
          </div>
        ) : (
          <div className="articles-grid">
            {results.map((a) => <ArticleCard key={a._id} article={a} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Articles;
